import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  ExternalLink,
  Clock,
  Target,
  Trophy,
  Users,
  DollarSign,
  Loader2,
  Square,
  FileText,
  Settings,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useDailyReportStore } from "../store/useDailyReportStore";
import { useAuthStore } from "../store/useAuthStore";

const fmtMoney = (n: number) => {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  return `$${Math.round(n).toLocaleString()}`;
};

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const DailyReportListPage = () => {
  const navigate = useNavigate();
  const { reports, fetchReports, endShift, loading } = useDailyReportStore();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [endingShift, setEndingShift] = useState(false);
  const [notes, setNotes] = useState("");
  const [showEndModal, setShowEndModal] = useState(false);
  const { authUser } = useAuthStore();
  const isBD = authUser?.role === "Business Developer";

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleFilter = () => {
    const params: any = {};
    if (from) params.from = from;
    if (to) params.to = to;
    fetchReports(params);
  };

  const handleEndShift = async () => {
    setEndingShift(true);
    const r = await endShift(notes || undefined);
    setEndingShift(false);
    if (r) {
      setShowEndModal(false);
      setNotes("");
      navigate(`/reports/daily/${r._id}`);
    }
  };

  return (
    <div className={`min-h-screen bg-background px-4 lg:px-8 pb-10 ${isBD ? "pt-8" : "pt-20"}`}>
      <div className="max-w-[1400px] mx-auto">
        <div className="bg-surface-container-lowest rounded-xl border border-outline-variant card-shadow p-6 flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <h1 className="text-[26px] font-bold text-on-surface tracking-tight">
              Daily Reports
            </h1>
            <p className="text-[14px] text-on-surface-variant mt-1">
              End-of-shift performance reports — auto-distributed via email &amp; Slack
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/settings/integrations"
              className="inline-flex items-center gap-1.5 px-3 py-2.5 border border-slate-200 rounded-lg font-semibold text-[13px] text-slate-700 hover:bg-slate-50 transition"
            >
              <Settings size={14} />
              <span className="hidden sm:inline">Integrations</span>
            </Link>
            <button
              onClick={() => setShowEndModal(true)}
              className="inline-flex items-center gap-2 bg-[#0058c3] hover:opacity-90 text-white px-4 py-2.5 rounded-lg font-semibold text-[13px] transition shadow-sm"
            >
              <Square size={14} />
              <span className="hidden sm:inline">End Shift & Generate Report</span>
              <span className="sm:hidden">End Shift</span>
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)] p-4 mb-5 flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
              From
            </label>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-md text-[13px] bg-slate-50"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
              To
            </label>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-md text-[13px] bg-slate-50"
            />
          </div>
          <button
            onClick={handleFilter}
            className="px-4 py-2 bg-slate-900 text-white rounded-md text-[13px] font-semibold"
          >
            Apply
          </button>
          {(from || to) && (
            <button
              onClick={() => {
                setFrom("");
                setTo("");
                fetchReports();
              }}
              className="px-3 py-2 text-[12px] text-blue-600 hover:text-blue-800 font-semibold"
            >
              Clear
            </button>
          )}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[12px]">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">BD</th>
                  <th className="px-4 py-3 text-center">Hours</th>
                  <th className="px-4 py-3 text-center">Bids</th>
                  <th className="px-4 py-3 text-center">Won</th>
                  <th className="px-4 py-3 text-center">Leads</th>
                  <th className="px-4 py-3 text-center">Projects</th>
                  <th className="px-4 py-3 text-right">Revenue</th>
                  <th className="px-4 py-3 text-center">Delivered</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && reports.length === 0 ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-4 py-14 text-center text-slate-400"
                    >
                      <Loader2 size={18} className="inline animate-spin mr-2" />
                      Loading reports...
                    </td>
                  </tr>
                ) : reports.length === 0 ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-4 py-14 text-center text-slate-400"
                    >
                      No reports yet. End a shift to generate your first daily
                      report.
                    </td>
                  </tr>
                ) : (
                  reports.map((r) => (
                    <tr
                      key={r._id}
                      onClick={() => navigate(`/reports/daily/${r._id}`)}
                      className="hover:bg-slate-50/60 transition cursor-pointer"
                    >
                      <td className="px-4 py-3.5">
                        <div className="font-bold text-slate-900">
                          {fmtDate(r.workDate)}
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="text-slate-700">
                          {typeof r.bd === "object" ? r.bd?.fullName : "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <span className="inline-flex items-center gap-1 text-slate-700">
                          <Clock size={11} />
                          {r.totalWorkingHours}h
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-center font-semibold text-slate-800">
                        {r.bidding.totalBids}
                      </td>
                      <td className="px-4 py-3.5 text-center font-bold text-emerald-700">
                        {r.bidding.wonBids}
                      </td>
                      <td className="px-4 py-3.5 text-center text-slate-700">
                        {r.leads.totalLeads}
                      </td>
                      <td className="px-4 py-3.5 text-center text-slate-700">
                        {r.projects.projectsWon}
                      </td>
                      <td className="px-4 py-3.5 text-right font-bold text-[#006a61] font-mono">
                        {fmtMoney(r.financials.confirmedRevenue)}
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <span className="inline-flex items-center gap-1">
                          {r.deliveries.emailSent && (
                            <span
                              className="w-1.5 h-1.5 rounded-full bg-blue-500"
                              title="Email sent"
                            />
                          )}
                          {r.deliveries.slackSent && (
                            <span
                              className="w-1.5 h-1.5 rounded-full bg-violet-500"
                              title="Slack sent"
                            />
                          )}
                          {!r.deliveries.emailSent &&
                            !r.deliveries.slackSent && (
                              <span className="text-slate-300">—</span>
                            )}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-right">
                        <button className="text-blue-600 hover:text-blue-800 font-semibold inline-flex items-center gap-1 text-[11px]">
                          Open
                          <ExternalLink size={11} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showEndModal && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/30 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowEndModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6"
          >
            <div className="flex items-center gap-2 mb-3">
              <Square size={20} className="text-[#0058c3]" />
              <h2 className="text-[18px] font-extrabold text-slate-900">
                End Shift
              </h2>
            </div>
            <p className="text-[13px] text-slate-600 mb-4">
              This will generate your daily performance report and dispatch it
              via email + Slack.
            </p>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">
              Notes (optional)
            </label>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything to highlight from today..."
              className="w-full px-3 py-2 text-[13px] bg-slate-50 border border-slate-200 rounded-md resize-none mb-5"
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowEndModal(false)}
                disabled={endingShift}
                className="px-4 py-2 text-[12px] font-semibold border border-slate-200 text-slate-700 rounded-md hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleEndShift}
                disabled={endingShift}
                className="px-4 py-2 text-[12px] font-semibold bg-[#0058c3] hover:opacity-90 text-white rounded-md inline-flex items-center gap-1.5 disabled:opacity-60"
              >
                {endingShift ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FileText size={14} />
                    End Shift & Generate
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyReportListPage;
